import { qnorm, pnorm } from 'lib-r-math.js'
import type { BaseSpecies } from '../types'

export interface ViabilityConstants {
  Ke: number // Species-specific moisture constant
  Cw: number // Species-specific moisture constant
  Ch: number // Species-specific temperature constant
  Cq: number // Species-specific temperature constant
  moistureContent: number // At equilibrium
  temp: number
}
interface ViabilityInputs extends ViabilityConstants {
  initialViability: number
  storageDays: number
}

interface StorageTimeInputs extends ViabilityConstants {
  initialViability: number
  finalViability: number
}
interface EstimateMoistureContentInputs
  extends Omit<ViabilityConstants, 'moistureContent'> {
  initialViability: number
  finalViability: number
  storageDays: number
}

const percentageToProbit = (percentage: number) => qnorm(percentage / 100, 5, 1)

const probitToPercentage = (probit: number) => pnorm(probit, 5, 1) * 100

/**
Ellis RH, Roberts EH (1980). “Improved equations for the prediction of seed longevity.” Annals of Botany, 45(1), 13--30.
http://seed.agron.ntu.edu.tw/ENG/tech/Eviability.htm
*/

const getViabilityConstantsExponent = ({
  Ke,
  Cw,
  Ch,
  Cq,
  moistureContent,
  temp,
}: ViabilityConstants) =>
  Ke - Cw * Math.log10(moistureContent) - Ch * temp - Cq * Math.pow(temp, 2)

export const getFinalViability = ({
  Ke,
  Cw,
  Ch,
  Cq,
  moistureContent,
  temp,
  initialViability,
  storageDays,
}: ViabilityInputs): number => {
  const exponent = getViabilityConstantsExponent({
    Ke,
    Cw,
    Ch,
    Cq,
    moistureContent,
    temp,
  })
  return probitToPercentage(
    percentageToProbit(initialViability) - storageDays / Math.pow(10, exponent),
  )
}

export const getStorageDays = ({
  Ke,
  Cw,
  Ch,
  Cq,
  moistureContent,
  temp,
  initialViability,
  finalViability,
}: StorageTimeInputs) => {
  const exponent = getViabilityConstantsExponent({
    Ke,
    Cw,
    Ch,
    Cq,
    moistureContent,
    temp,
  })

  return (
    (percentageToProbit(initialViability) -
      percentageToProbit(finalViability)) *
    Math.pow(10, exponent)
  )
}

export const getMoistureContent = ({
  Ke,
  Cw,
  Ch,
  Cq,
  temp,
  initialViability,
  finalViability,
  storageDays,
}: EstimateMoistureContentInputs) => {
  const numerator =
    -Cq * Math.pow(temp, 2) * Math.log(10) -
    Math.log(storageDays / (initialViability - finalViability))

  return Math.pow(
    10,
    -((Ch * temp) / Cw) + Ke / Cw + numerator / (Cw * Math.log(10)),
  )
}

export const getDaysToLoseOneProbit = (args: ViabilityConstants) =>
  Math.pow(10, getViabilityConstantsExponent(args))

/**
Moisture equililbrium calculation follows
https://www.bioversityinternational.org/fileadmin/_migrated/uploads/tx_news/The_design_of_seed_storage_facilities_for_genetic_conservation_281.pdf page 45, fig 3.5.
*/
export const getMoistureEquilibrium = ({
  oilContent,
  temp,
  relativeHumidity,
}: {
  oilContent: number
  temp: number
  relativeHumidity: number
}) => {
  const moistureContent =
    ((1 - oilContent / 100) *
      Math.sqrt(-440 * Math.log(1 - relativeHumidity / 100))) /
    (1.1 + temp / 90)
  return (100 * moistureContent) / (100 + moistureContent)
}

export const getTaxonNameDisplay = ({
  genus,
  epithet,
  infraspecies_rank,
  infraspecies_epithet,
}: BaseSpecies) =>
  `${genus} ${epithet}${
    infraspecies_rank ? ` ${infraspecies_rank} ${infraspecies_epithet}` : ''
  }`

const defaultExport = {}
export default defaultExport
